import { Component } from '@theme/component';
import { ThemeEvents, CartQuantityRefreshedEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { themeRoutes } from '@theme/ca-cart-add';

/**
 * <announcement-shipping> — free-shipping progress for the announcement bar.
 *
 * Server-renders the initial message/progress from `cart.total_price`, then updates live on
 * `ThemeEvents.cartUpdate`. As of the 2026-07-26 add-to-cart perf fix, the product-form-component
 * fast path dispatches that event with `detail.resource: undefined` (avoids a blocking /cart.js
 * refetch before the drawer can open) and follows up with a separate `CartQuantityRefreshedEvent`
 * once its own fire-and-forget /cart.js call resolves — we listen for that instead of self-fetching
 * here, which would be the exact redundant call the perf fix removed elsewhere. Cart-page
 * quantity/remove edits (still a section-render payload with no `total_price` on `cart:update`)
 * are the one remaining case that needs the self-fetch fallback in #fetchTotal.
 *
 * Data attributes:
 *  - data-threshold:    free-shipping threshold in minor units (cents)
 *  - data-money-format: shop money format string (for formatMoney)
 *  - data-currency:     ISO currency code
 *  - data-message:      message before the threshold; `[amount]` is replaced
 *  - data-success:      message once the threshold is reached
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [text]
 * @property {HTMLElement} [fill]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementShipping extends Component {
  #handler = (event) => this.#onCartUpdate(event);

  connectedCallback() {
    super.connectedCallback();

    this.threshold = Number(this.dataset.threshold) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.currency = this.dataset.currency || 'USD';
    this.message = this.dataset.message || '';
    this.success = this.dataset.success || '';

    document.addEventListener(ThemeEvents.cartUpdate, this.#handler);
    document.addEventListener(CartQuantityRefreshedEvent.eventName, this.#quantityRefreshedHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handler);
    document.removeEventListener(CartQuantityRefreshedEvent.eventName, this.#quantityRefreshedHandler);
  }

  #quantityRefreshedHandler = (event) => this.#onQuantityRefreshed(event);

  /**
   * @param {Event} event
   */
  #onCartUpdate(event) {
    const cart = /** @type {any} */ (event)?.detail?.resource;
    if (cart && typeof cart.total_price === 'number') {
      this.#update(cart.total_price);
      return;
    }

    // CARLYLE — J: the product-form-component add-to-cart fast path is the source of most
    // `resource`-less `cart:update` dispatches now; it always follows up with
    // CartQuantityRefreshedEvent (see #onQuantityRefreshed), so don't self-fetch for it — that's
    // the redundant /cart.js call the 2026-07-26 perf fix removed elsewhere. Only genuinely
    // resource-less sources (cart-page quantity/remove edits) fall through to #fetchTotal.
    if (/** @type {any} */ (event)?.detail?.data?.source === 'product-form-component') return;

    this.#fetchTotal();
  }

  /**
   * @param {Event} event
   */
  #onQuantityRefreshed(event) {
    const cart = /** @type {any} */ (event)?.detail?.resource;
    if (cart && typeof cart.total_price === 'number') this.#update(cart.total_price);
    // undefined resource = the refresh fetch itself failed (see product-form.js); nothing to do —
    // the announcement bar just keeps showing the last-known total rather than hanging.
  }

  #fetchTotal() {
    const base = themeRoutes().cart_url || '/cart';
    fetch(`${base}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart && typeof cart.total_price === 'number') this.#update(cart.total_price);
      })
      .catch(() => {});
  }

  /**
   * @param {number} totalPrice - cart total in minor units (cents)
   */
  #update(totalPrice) {
    const remaining = Math.max(0, this.threshold - totalPrice);
    const { text, fill } = this.refs;
    const reached = this.threshold > 0 && remaining <= 0;

    // Drive state-dependent styling (check icon, weight) on live updates too, not just SSR.
    this.classList.toggle('is-reached', reached);

    if (text) {
      if (reached) {
        text.textContent = this.success;
      } else {
        let money = '';
        try {
          money = formatMoney(remaining, this.moneyFormat, this.currency);
        } catch (error) {
          money = '';
        }
        // Wrap the amount so it can be bolded; escape both parts since we assign innerHTML.
        text.innerHTML = AnnouncementShipping.#escape(this.message).replace(
          '[amount]',
          `<span class="ca-ship-amount">${AnnouncementShipping.#escape(money)}</span>`
        );
      }
    }

    if (fill && this.threshold > 0) {
      const pct = Math.min(100, Math.round((totalPrice / this.threshold) * 100));
      fill.style.width = `${pct}%`;
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  static #escape(value) {
    return String(value).replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
    );
  }
}

if (!customElements.get('announcement-shipping')) {
  customElements.define('announcement-shipping', AnnouncementShipping);
}
